// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer external ID editor component with inline display and edit capability.
 * Follows the same pattern as customer comments with EditablePropertyButton and PopoverForm.
 */
import { EditablePropertyButton } from '@/components/editable-property-button';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import { Customer } from '@/customer/api/types';
import {
  UpdateCustomerParameters,
  updateCustomer,
} from '@/customer/api/update-customer';
import { useDisclosure } from '@/hooks/use-disclosure';
import { usePopoverAutoFocus } from '@/hooks/use-popover-auto-focus';
import { useTimeout } from '@/hooks/use-timeout';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, Typography, useTheme } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const EMPTY_STATE = 'External ID not set';

const formSchema = z.object({ externalId: z.string().nullable() });

type FormData = z.infer<typeof formSchema>;

interface Props {
  customer: Pick<Customer, 'key' | 'externalId'>;
}

/**
 * Customer external ID editor with inline display and edit capability.
 * Displays the external ID with an edit button that opens a popover form for editing.
 * Handles empty states and provides a single-line input experience.
 */
export function CustomerExternalIdEditor(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = usePopoverAutoFocus(editor.isOpen);
  const queryClient = useQueryClient();

  const { formState, register, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { externalId: props.customer.externalId ?? '' },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: (parameters: UpdateCustomerParameters) =>
      updateCustomer(props.customer.key, parameters),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer'] });
      reset({ externalId: data.customer.externalId ?? '' });
      editor.close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const { isDirty } = formState;

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (isDirty) {
      const externalId = data.externalId === '' ? null : data.externalId;

      return updateCustomerMutation.mutate({ externalId });
    }

    editor.close();
  };

  const externalId = props.customer.externalId ?? EMPTY_STATE;
  const color =
    props.customer.externalId === null ? 'text.secondary' : 'text.primary';

  return (
    <>
      <EditablePropertyButton
        ref={buttonRef}
        onClick={editor.open}
        sx={{
          display: 'flex',
          minWidth: '15rem',
          minHeight: '2.5rem',
          alignItems: 'center',
          justifyContent: 'start',
          textAlign: 'left',
          borderLeft: '2px solid',
          borderColor: 'divider',
          borderRadius: 0,
          position: 'relative',
          '& .edit-icon': {
            position: 'absolute',
            top: '0.8rem',
            right: '0.3rem',
            margin: 0,
          },
        }}
      >
        <Typography
          variant="body2"
          textAlign="left"
          marginRight="auto"
          color={color}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {externalId}
        </Typography>
      </EditablePropertyButton>
      <SupportSnackbar
        isOpen={updateCustomerMutation.isError}
        onClose={updateCustomerMutation.reset}
        message="An error occurred while updating the customer external ID"
      />
      <PopoverForm
        verticalOffset={24}
        horizontalOffset={8}
        position="left"
        verticalPosition="top"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
      >
        <TextField
          autoFocus
          inputRef={inputRef}
          size="small"
          InputProps={register('externalId')}
          sx={{ width: '20rem' }}
          placeholder="Set external ID"
        />
      </PopoverForm>
    </>
  );
}
